import { StaticImage } from 'gatsby-plugin-image';
import type { FC } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AddOnsTemplate } from '@/components/add-ons/template';
import { GalleriesItem } from '@/components/galleries/item';
import { GalleriesList } from '@/components/galleries/list';
import { ComponentsHead } from '@/components/head';
import { AnchorWithTitle } from '@/components/links/anchor';
import { LinkWithTitle } from '@/components/links';

export const CHANNELSIDE_DISTRICT_TITLE =
  'Channelside District';

export const Head = () => {
  return (
    <ComponentsHead
      title="Boat to Channelside District | Music, Drinks & City Views"
      description="Dock at Channelside with a private charter. Enjoy music, bars, and a front-row seat to Tampa’s most electric nightlife and scenic waterfront."
      keywords="Channelside, Nightlife, Music, Dock and Dine, Party Cruise"
    />
  );
};
const Page: FC = () => {
  return (
    <AddOnsTemplate
      title="Channelside District Boat Charters – Cruise Tampa’s Hottest Waterfront Strip"
      subtitle=""
    >
      <GalleriesList>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${CHANNELSIDE_DISTRICT_TITLE} header`}
              src="../../images/areas/channelside-district/0.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
        <SwiperSlide>
          <GalleriesItem>
            <StaticImage
              alt={`${CHANNELSIDE_DISTRICT_TITLE} slide 1`}
              src="../../images/areas/channelside-district/1.jpg"
            />
          </GalleriesItem>
        </SwiperSlide>
      </GalleriesList>
      <p>
        A charter through the{' '}
        <AnchorWithTitle href="https://www.visittampabay.com/channel-district/">
          Channelside District
        </AnchorWithTitle>{' '}
        gives you a front-row seat to one of Tampa’s
        liveliest waterfront destinations. Nestled alongside
        the Garrison Channel, this area is home to the
        Florida Aquarium, Sparkman Wharf, and the cruise
        ship terminals—offering a unique blend of
        entertainment, history, and stunning views. Our
        Tampa Bay boat charters cruise through the heart of
        this vibrant neighborhood, offering a fresh,
        exciting way to experience the city.
      </p>
      <p>
        With the bustling backdrop of high-rises and
        water-facing restaurants, a cruise through
        Channelside is ideal for both sightseeing and
        soaking up the electric atmosphere of the area.
        You’ll glide past luxury yachts, modern
        developments, and hidden gems that are only visible
        from the water. Depending on the vibe of your trip,
        we can keep it relaxed and scenic or lean into
        something more upbeat and celebratory.
      </p>
      <h3>
        Available Charters Through Channelside District:
      </h3>
      <ul>
        <li>
          <LinkWithTitle to="/services/8-hour-private-party-boat-charter/">
            8-Hour Private Party Boat
          </LinkWithTitle>
          : Make a day of it! Cruise through Channelside and
          beyond, with plenty of time for sightseeing,
          swimming, or anchoring at one of Tampa Bay’s chill
          sandbars.
        </li>
        <li>
          <LinkWithTitle to="/services/6-hour-private-party-boat-charter">
            6-Hour Private Party Boat
          </LinkWithTitle>
          : The perfect balance between time and
          exploration. Spend the afternoon winding through
          city waterways before heading out toward the open
          bay.
        </li>
        <li>
          <LinkWithTitle to="/services/4-hour-private-party-boat-charter/">
            4-Hour Private Party Boat
          </LinkWithTitle>
          : A half-day cruise that still packs in the
          sights. Ideal for seeing Channelside’s highlights
          without committing to a full day.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-dj-nightime-party-boat-charter">
            3-Hour Sunset Charter
          </LinkWithTitle>
          : Witness the glow of the sun dipping behind
          Tampa’s skyline while the Channelside lights begin
          to twinkle. It’s a romantic and relaxing cruise
          option.
        </li>
        <li>
          <LinkWithTitle to="/services/3-hour-wave-rave-dj-nightime-party-boat-charter">
            3-Hour Wave Rave Charter
          </LinkWithTitle>
          : High energy meets waterfront cityscape. Enjoy
          the vibe, music, and nonstop views as we skim
          through Tampa’s most iconic waterway.
        </li>
        <li>
          <LinkWithTitle to="/services/private-snorkeling-charters-st-petersburg/">
            Snorkeling Charters
          </LinkWithTitle>
          : While Channelside itself isn’t ideal for
          snorkeling, we can cruise to a nearby island for a
          swim and some underwater exploration.
        </li>
        <li>
          <LinkWithTitle to="/services/large-group-charter-boat/">
            Large Group Boat Charter
          </LinkWithTitle>
          : Hosting a big event or group outing? Let us show
          your guests the Channelside District from the
          comfort of a luxury vessel—music, views, and great
          company included.
        </li>
      </ul>
      <p>
        With its buzzing harbor energy and postcard-worthy
        skyline, the Channelside District is a Tampa
        treasure. Book a charter with Tampa Bay Boating
        Adventures and see the city’s vibrant edge like
        never before.
      </p>
    </AddOnsTemplate>
  );
};

export default Page;
